// Status color mappings based on the design requirements
export interface StatusColor {
  bg: string;
  text: string;
  border: string;
  label?: string;
}

/*
 * Shared workflow status palette.
 *
 * Provenance:
 * - These statuses were already widely used before the QC token migration.
 * - The token families below are intended to preserve the prior hardcoded classes:
 *   - `attention` ~= `bg-orange-50 text-orange-700 border-orange-200`
 *   - `progress`  ~= `bg-blue-50 text-blue-700 border-blue-200`
 *   - `notice`    ~= `bg-yellow-50 text-yellow-700 border-yellow-200`
 *   - `positive`  ~= `bg-green-50 text-green-700 border-green-200`
 *   - `critical`  ~= `bg-red-50 text-red-700 border-red-200`
 *
 * Treat this file as the source of truth for existing shared status colors.
 */
export const STATUS_COLORS: Record<string, StatusColor> = {
  // Gray states
  created: {
    bg: 'bg-muted',
    text: 'text-foreground',
    border: 'border',
  },
  human_call: {
    bg: 'bg-attention',
    text: 'text-attention-foreground',
    border: 'border-attention-border',
  },
  ai_call: {
    bg: 'bg-progress',
    text: 'text-progress-foreground',
    border: 'border-progress-border',
    label: 'AI Call In-Progress',
  },

  // Blue/Purple states (AI processing)

  pre_processing: {
    bg: 'bg-progress',
    text: 'text-progress-foreground',
    border: 'border',
  },
  ai_audit: {
    bg: 'bg-progress',
    text: 'text-progress-foreground',
    border: 'border-progress-border',
  },

  // Orange states (Human intervention needed)
  human_audit: {
    bg: 'bg-notice',
    text: 'text-notice-foreground',
    border: 'border-notice-border',
    label: 'Human Audit Needed',
  },
  input_audit_needed: {
    bg: 'bg-attention',
    text: 'text-attention-foreground',
    border: 'border',
  },

  human_step_missing_info: {
    bg: 'bg-attention',
    text: 'text-attention-foreground',
    border: 'border',
  },

  // Green state (Success)
  completed: {
    bg: 'bg-positive',
    text: 'text-positive-foreground',
    border: 'border-positive-border',
  },

  // Red state (Failure/Termination)
  terminated: {
    bg: 'bg-critical',
    text: 'text-critical-foreground',
    border: 'border-critical-border',
  },

  // Eligibility check states
  eligibility_check: {
    bg: 'bg-progress',
    text: 'text-progress-foreground',
    border: 'border-progress-border',
    label: 'Eligibility Check In Progress',
  },
  eligibility_check_human_audit: {
    bg: 'bg-notice',
    text: 'text-notice-foreground',
    border: 'border-notice-border',
    label: 'Eligibility Check Human Audit',
  },
};

// Normalize state names to handle different formats (camelCase, snake_case, etc.)
export const normalizeStateName = (state: string): string => {
  if (!state) return '';

  // Convert camelCase or PascalCase to snake_case
  return state
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1_$2')
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/-/g, '_');
};

// Get status colors with fallback
// Supports phase-specific states by stripping phase prefix (e.g., "primary_human_call" → "human_call")
export const getStatusColorConfig = (state: string | undefined): StatusColor => {
  if (!state) {
    return {
      bg: 'bg-muted',
      text: 'text-foreground',
      border: 'border',
    };
  }

  const normalizedState = normalizeStateName(state);

  // Check for phase-specific states and map to base state
  // Strips "primary_" or "secondary_" prefix to match base state colors
  const baseState = normalizedState.replace(/^(primary|secondary)_/, '');

  // Special handling for eligibility check states
  if (normalizedState.includes('eligibility_check')) {
    if (normalizedState.includes('human_audit')) {
      return STATUS_COLORS['eligibility_check_human_audit'];
    } else {
      return STATUS_COLORS['eligibility_check'];
    }
  }

  // Try exact match first, then base state (without phase prefix), then default gray
  return (
    STATUS_COLORS[normalizedState] ||
    STATUS_COLORS[baseState] || {
      bg: 'bg-muted',
      text: 'text-foreground',
      border: 'border',
    }
  );
};
